import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Flame,
  Mountain,
  ShieldCheck,
  Users,
  Cpu,
  Wifi,
  MonitorSmartphone,
  SunMedium,
  Fuel,
  Route,
  Building2,
  House,
  Compass,
  ChevronDown,
  Gauge,
  Wrench,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import EmiCalculator from "@/components/EmiCalculator";
import Countdown from "@/components/Countdown";
import HotspotView from "@/components/HotspotView";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import { api, formatINR } from "@/lib/api";

const IMG = {
  hero: "/images/suv-forest.jpg",
  suv: "/images/panel-power.jpg",
  rear: "/images/panel-anywhere.jpg",
  interior: "/images/panel-interior.jpg",
  offroad: "/images/panel-offroad.jpg",
  highway: "/images/suv-offroad.jpg",
  cockpit: "/images/panel-interior.jpg",
  detail: "/images/panel-power.jpg",
  aeroD: "/images/aero-hero.jpg",
  aeroRear: "/images/aero-rear.jpg",
  seats: "/images/seats.jpg",
  aeroBlack: "/images/aero-black.jpg",
  aeroWhite: "/images/aero-white.jpg",
  aeroSilver: "/images/aero-silver.jpg",
};

const POSTERS = {
  aero: "/images/poster-aero.jpg",
  suv: "/images/poster-suv.jpg",
  electric: "/images/poster-ev.jpg",
};

const HERO_LINES = [
  { text: "POWER", gold: false },
  { text: "MEETS", gold: true },
  { text: "PURPOSE.", gold: false },
];

const HINDI_TAGLINE = "नई सोच... हर रास्ते के लिए एक बेहतर कल";

const FEATURES = [
  { icon: Flame, tag: "01", title: "Powerful Diesel", desc: "High torque, high efficiency. 2.0L / 3.0L diesel — built to move loads and miles.", span: "md:col-span-2" },
  { icon: Mountain, tag: "02", title: "All Terrain Capability", desc: "City, highway, mountain, village — every road is home turf.", span: "" },
  { icon: ShieldCheck, tag: "03", title: "Advanced Safety", desc: "360° protection with ADAS and advanced safety systems.", span: "" },
  { icon: Users, tag: "04", title: "Premium Comfort", desc: "Spacious 7-seater cabin — comfort for every journey, for everyone.", span: "md:col-span-2" },
  { icon: Cpu, tag: "05", title: "AI Drive Assist", desc: "Smart and safe driving — assistance that watches the road with you.", span: "" },
  { icon: Wifi, tag: "06", title: "Smart Connectivity", desc: "App, navigation and remote control — always connected.", span: "" },
  { icon: MonitorSmartphone, tag: "07", title: "Digital Cockpit", desc: "Dual-screen next-gen cockpit. Technology meets luxury.", span: "" },
  { icon: SunMedium, tag: "08", title: "Ambient Lighting", desc: "64 colors of mood, inside your cabin.", span: "" },
  { icon: Fuel, tag: "09", title: "Fuel Efficient", desc: "More km, lower cost. Low maintenance, built for Indian roads.", span: "" },
];

const MODELS = [
  {
    img: IMG.aeroD,
    name: "IshuCar AERO D",
    tag: "Crossover SUV",
    line: "नई सोच... हर रास्ते के लिए एक बेहतर कल",
    spec: "2.0L / 3.0L Diesel · RWD / AWD",
    poster: POSTERS.aero,
    slug: "aero-d",
  },
  {
    img: "/images/suv-forest.jpg",
    name: "IshuCar SUV",
    tag: "Off-Road 4x4 AWD · Diesel",
    line: "ताकत साथ है — अब हर terrain पर",
    spec: "₹50 Lakh* onwards · EXPLORER | ADVENTURE | SIGNATURE",
    poster: null,
    slug: "suv",
  },
  {
    img: "/images/ev-hero.jpg",
    name: "IshuCar Electric",
    tag: "100% Electric · 700+ km",
    line: "पुराना जादू, नई ताकत — अब बिजली पर",
    spec: "700+ km Range · AI Copilot · Fast Charging",
    poster: POSTERS.electric,
    slug: "electric",
  },
  {
    img: "/images/ishustar-hero.jpg",
    name: "IshuCar ISHUSTAR",
    tag: "Electric Hypercar",
    line: "सपनों का हाइपरकार — अब हक़ीक़त में",
    spec: "₹2,55,24,000 · Single Variant · Limited",
    poster: null,
    slug: "ishustar",
  },
];

const VARIANTS = [
  { img: IMG.aeroWhite, name: "AERO D ONE", tier: "Premium", ex: "₹12.74 Lakh*", was: "₹14.99 Lakh*", onroad: "₹15.10 Lakh*" },
  { img: IMG.aeroBlack, name: "AERO D PRO", tier: "Performance", ex: "₹16.99 Lakh*", was: "₹19.99 Lakh*", onroad: "₹20.10 Lakh*" },
  { img: IMG.aeroSilver, name: "AERO D X", tier: "Adventure", ex: "₹21.24 Lakh*", was: "₹24.99 Lakh*", onroad: "₹25.11 Lakh*" },
  { img: IMG.aeroRear, name: "AERO D LUXE", tier: "Luxury", ex: "₹25.49 Lakh*", was: "₹29.99 Lakh*", onroad: "₹30.10 Lakh*" },
];

const CABIN = [
  { img: IMG.interior, title: "Next-Gen Interior", sub: "Technology meets luxury — triple screens, premium leather finish.", hindi: "टेक्नोलॉजी मिलती है लग्ज़री से" },
  { img: IMG.seats, title: "Spacious 7 Seater", sub: "Comfort for every journey — panoramic roof, ventilated seats.", hindi: "हर सफ़र के लिए आराम" },
];

const TERRAIN = [
  { icon: Mountain, name: "Mountain", hindi: "पहाड़", desc: "Conquer higher" },
  { icon: Route, name: "Highway", hindi: "राजमार्ग", desc: "Effortless & powerful" },
  { icon: Building2, name: "City", hindi: "शहर", desc: "Made for every street" },
  { icon: House, name: "Village", hindi: "गाँव", desc: "सभी सड़कों के लिए तैयार" },
  { icon: Compass, name: "Off-Road", hindi: "ऑफ-रोड", desc: "Built to explore" },
];

const GALLERY = [
  { img: IMG.rear, title: "Bold Rear Design", sub: "हर मोड़ पर एक नई पहचान — LED light bar, dual exhaust look." },
  { img: IMG.interior, title: "Next-Gen Interior", sub: "Technology meets luxury. Digital cockpit, 64-color ambient lighting." },
  { img: IMG.offroad, title: "Water Wading", sub: "Go anywhere — built to explore. कहीं भी, कभी भी।" },
  { img: IMG.highway, title: "Highway Glide", sub: "Effortless and powerful — every single day." },
];

function Hero() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const carY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const carScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={heroRef} className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-16" data-testid="hero-section">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="/images/terrain-x-poster.jpg"
        data-testid="hero-video"
        className="absolute inset-0 h-full w-full object-cover opacity-30 sm:opacity-40"
        aria-hidden="true"
      >
        <source src="/videos/terrain-x-hero.mp4" type="video/mp4" />
        <source src="/videos/terrain-x-hero.webm" type="video/webm" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-[#070709]/80 via-[#070709]/60 to-[#070709]" aria-hidden="true" />
      <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_78%)]" aria-hidden="true" />
      <div
        className="glow-pulse absolute left-1/2 top-1/3 h-[520px] w-[820px] -translate-x-1/2 rounded-full blur-[140px]"
        style={{ background: "radial-gradient(ellipse, rgba(230,184,0,0.16), transparent 65%)" }}
        aria-hidden="true"
      />

      <motion.div style={{ opacity: contentFade }} className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="eyebrow"
          >
            IshuCar SUV — Premium Diesel SUV
          </motion.p>

          <h1 className="mt-6 font-display text-[32px] font-extrabold uppercase leading-[1.06] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-[60px]" data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.gold ? "text-gold-gradient" : ""} ${line.small ? "text-[0.72em] leading-[1.3]" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="font-hindi mt-4 text-xl font-bold text-[#e6b800] sm:text-2xl"
            data-testid="hero-hindi-tagline"
          >
            {HINDI_TAGLINE}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-6 max-w-lg text-base leading-relaxed text-neutral-400 sm:text-lg"
            data-testid="hero-subline"
          >
            The IshuCar SUV — powerful diesel, all-terrain capability and a
            spacious 7-seater cabin. Premium SUV बना आपके हर रास्ते का साथी।
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/booking"
              data-testid="hero-reserve-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-7 py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_36px_rgba(230,184,0,0.45)]"
            >
              Reserve for {formatINR(10000)}
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/features"
              data-testid="hero-explore-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-semibold text-white transition-all duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            >
              Explore the machine
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-7"
            data-testid="hero-stats"
          >
            {[
              { icon: Fuel, value: "2.0L / 3.0L", label: "Diesel engine" },
              { icon: Route, value: "RWD / AWD", label: "All terrain" },
              { icon: Users, value: "7 Seater", label: "Spacious cabin" },
            ].map((s) => (
              <div key={s.label}>
                <s.icon size={16} className="text-[#e6b800]" />
                <p className="mt-2 font-mono2 text-lg font-semibold text-white sm:text-xl">{s.value}</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-neutral-500">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            style={{ y: carY, scale: carScale }}
            className="relative"
            data-testid="hero-vehicle"
          >
            <div className="tick-frame overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.6)]">
              <img
                src={IMG.hero}
                alt="IshuCar SUV — Premium Diesel SUV"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
            <p className="mt-4 text-right font-mono2 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
              IshuCar SUV · Premium Diesel SUV
            </p>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 text-neutral-600"
        aria-hidden="true"
      >
        <ChevronDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function Variants() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="variants-section">
      <SectionHeading
        eyebrow="Variants"
        title="One brand. Four characters."
        sub="Pick the IshuCar that matches your life — Premium, Performance, Adventure or Luxury. हर रास्ते के लिए एक AERO D।"
      />
      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {VARIANTS.map((v, i) => (
          <Reveal key={v.name} delay={i * 0.08}>
            <div className="group overflow-hidden rounded-2xl border border-white/10 bg-[#111116] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#e6b800]/50" data-testid={`variant-card-${i}`}>
              <div className="overflow-hidden">
                <img
                  src={v.img}
                  alt={v.name}
                  loading="lazy"
                  className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#e6b800]">{v.tier}</p>
                <h3 className="mt-1.5 font-display text-lg font-bold text-white">{v.name}</h3>
                <p className="mt-3 font-mono2 text-sm font-semibold text-white" data-testid={`variant-price-${i}`}>
                  <s className="mr-1.5 text-[11px] font-normal text-neutral-500">{v.was}</s>{v.ex}
                  <span className="ml-1.5 rounded bg-[#e6b800]/15 px-1 py-0.5 font-mono2 text-[9px] font-bold text-[#e6b800]">15% OFF</span>
                </p>
                <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-neutral-500">Est. on-road {v.onroad}*</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-[11px] leading-relaxed text-neutral-600" data-testid="variant-pricing-note">
        *Estimated on-road price range, indicative only — includes insurance, RTO and other charges. Ex-showroom price excludes them.
      </p>
      <Reveal className="mt-10 text-center">
        <Link
          to="/gallery#variant-posters"
          data-testid="variants-posters-link"
          className="group inline-flex items-center gap-2 rounded-full border border-[#e6b800]/40 px-7 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#e6b800] transition-colors duration-200 hover:bg-[#e6b800] hover:text-black"
        >
          चारों variants के posters देखें
          <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </section>
  );
}

function BentoFeatures() {
  return (
    <section className="border-y border-white/10 bg-[#0a0a0e] py-24 lg:py-32" data-testid="bento-features-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="खूबियाँ · Features"
          title="Power meets purpose"
          sub="2.0L / 3.0L diesel, all-terrain capability और 7-seater comfort — हर रास्ते के लिए तैयार।"
        />
        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 0.07} className={f.span}>
              <div
                className="group h-full rounded-2xl border border-white/10 bg-[#111116] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#e6b800]/50 hover:shadow-[0_18px_50px_rgba(230,184,0,0.08)]"
                data-testid={`bento-feature-${i}`}
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800] transition-all duration-300 group-hover:bg-[#e6b800] group-hover:text-[#070709]">
                    <f.icon size={20} />
                  </span>
                  <span className="font-mono2 text-[11px] text-neutral-600">{f.tag}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CabinShowcase() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="cabin-section">
      <SectionHeading
        eyebrow="अंदर की दुनिया · Inside"
        title="Panoramic space, premium at heart"
        sub="7-seater cabin — panoramic roof, ventilated seats और 64-color ambient lighting के साथ।"
      />
      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
        {CABIN.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div className="group relative overflow-hidden rounded-2xl border border-white/10" data-testid={`cabin-image-${i}`}>
              <img
                src={c.img}
                alt={c.title}
                loading="lazy"
                className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-96"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070709]/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-display text-lg font-bold uppercase text-white">{c.title}</h3>
                <p className="mt-1 text-sm text-neutral-400">{c.sub}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function GallerySpotlight() {
  return (
    <section className="border-t border-white/10 bg-[#0a0a0e] py-24 lg:py-32" data-testid="home-gallery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow="Design" title="Sculpted, not styled" sub="Every surface has an opinion. Look closer." />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {GALLERY.map((g, i) => (
            <Reveal key={g.title} delay={(i % 2) * 0.08}>
              <div className="group relative overflow-hidden rounded-2xl border border-white/10" data-testid={`home-gallery-item-${i}`}>
                <img
                  src={g.img}
                  alt={g.title}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-80"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070709]/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="font-display text-lg font-bold uppercase text-white">{g.title}</h3>
                  <p className="mt-1 text-sm text-neutral-400">{g.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center" testId="home-gallery-more">
          <Link
            to="/gallery"
            data-testid="home-gallery-link"
            className="inline-flex items-center gap-2 font-mono2 text-[12px] uppercase tracking-[0.25em] text-[#e6b800] transition-colors hover:text-[#ffc700]"
          >
            Open the full gallery <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function HotspotSection() {
  const AERO_HOTSPOTS = [
    { id: "headlamp", x: 73, y: 54, tag: "Light", title: "Adaptive LED Signature", desc: "आपके रास्ते को रोशन करें — adaptive LED headlamps, auto high-beam के साथ।" },
    { id: "cabin", x: 50, y: 40, tag: "Cabin", title: "Triple Digital Cockpit", desc: "Triple screens + 64-color ambient lighting — cockpit जो lounge जैसा लगे।" },
    { id: "roof", x: 47, y: 27, tag: "Roof", title: "Panoramic Sunroof", desc: "आसमान तक खुला नज़ारा — panoramic sunroof standard।" },
    { id: "wheel", x: 70, y: 76, tag: "Grip", title: "All-Terrain Grip", desc: "Aero alloys + tyre pressure monitoring — हर सतह पर पकड़।" },
    { id: "engine", x: 63, y: 46, tag: "Power", title: "2.0L / 3.0L Diesel", desc: "High torque engine — BS6 Phase 2, automatic gearbox के साथ।" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="hotspot-section-home">
      <Reveal>
        <p className="eyebrow text-center">Touch to reveal the invisible</p>
        <h2 className="mt-4 text-center font-display text-3xl font-extrabold uppercase text-white sm:text-5xl">
          अदृश्य को छू कर <span className="text-gold-gradient">देखिए</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-sm text-neutral-400 sm:text-base">
          Car पर चमकते points touch करीए — हर point के पीछे एक कहानी है।
        </p>
      </Reveal>
      <Reveal className="mt-12">
        <HotspotView
          image="/images/aero-hero.jpg"
          alt="IshuCar AERO D — touch to reveal"
          hotspots={AERO_HOTSPOTS}
          eyebrow="Car पर points touch करें"
          testid="hotspot-view-home"
        />
      </Reveal>
    </section>
  );
}

function UspStrip() {
  const ITEMS = [
    { id: "servicing", icon: Wrench, title: "₹300 Lifetime Servicing", desc: "सभी cars — हर service सिर्फ ₹300, lifetime" },
    { id: "battery", icon: ShieldCheck, title: "15-Year Battery Warranty", desc: "Electric battery पर 15 साल की lifetime warranty" },
    { id: "autodrive", icon: Cpu, title: "Auto Drive Mode", desc: "Highway पर car खुद चले — आप relax करें" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20" data-testid="usp-strip-section">
      <div className="grid gap-5 md:grid-cols-3">
        {ITEMS.map((u) => (
          <Reveal key={u.id}>
            <div className="card-glow flex h-full items-center gap-4 rounded-2xl p-6" data-testid={`usp-${u.id}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
                <u.icon size={18} />
              </span>
              <div>
                <p className="font-display text-sm font-bold uppercase text-white">{u.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-neutral-400">{u.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ReserveCTA() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    api
      .get("/bookings/count")
      .then((res) => setCount(res.data.confirmed))
      .catch(() => setCount(null));
  }, []);

  return (
    <section className="relative overflow-hidden py-28 lg:py-36" data-testid="reserve-cta-section">
      <div
        className="glow-pulse absolute left-1/2 top-1/2 h-[420px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[130px]"
        style={{ background: "radial-gradient(ellipse, rgba(230,184,0,0.14), transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <p className="eyebrow">प्री-बुकिंग खुली है · Pre-Booking Open</p>
          <h2 className="mt-5 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
            Reserve your <span className="text-gold-gradient">IshuCar</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg">
            Fully refundable token of {formatINR(10000)} के साथ अपनी जगह पक्की करें — नई सोच, नए रास्तों के लिए।
            {count > 0 ? ` ${count} ${count === 1 ? "person has" : "people have"} already reserved.` : " Be among the first owners."}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/booking"
              data-testid="reserve-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-8 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
            >
              Reserve your IshuCar
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/gallery"
              data-testid="reserve-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 font-semibold text-white transition-all duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            >
              <Gauge size={17} /> See it in detail
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ModelsSection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="models-section">
      <SectionHeading
        eyebrow="तीन किरदार · The Lineup"
        title="AERO D · SUV · ELECTRIC · ISHUSTAR"
        sub="दो प्रीमियम डीज़ल SUV, एक इलेक्ट्रिक आइकन और एक हाइपरकार — हर रास्ते के लिए एक साथी।"
      />
      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {MODELS.map((m, i) => (
          <Reveal key={m.name} delay={i * 0.1} className="h-full">
            <Link
              to={`/booking/${m.slug}`}
              data-testid={`model-card-${m.slug}`}
              className="group block h-full overflow-hidden rounded-2xl border border-white/10 bg-[#111116] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#e6b800]/60"
            >
              <div className="overflow-hidden">
                <img src={m.img} alt={m.name} loading="lazy" className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-56" />
              </div>
              <div className="p-7">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#e6b800]">{m.tag}</p>
                <h3 className="mt-1.5 font-display text-2xl font-bold text-white">{m.name}</h3>
                <p className="font-hindi mt-2 text-lg font-semibold text-[#e6b800]/90">{m.line}</p>
                <p className="mt-2 font-mono2 text-[11px] uppercase tracking-[0.18em] text-neutral-500">{m.spec}</p>
                <p className="mt-4 font-mono2 text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                  Book now <ArrowRight size={13} className="ml-1 inline text-[#e6b800] transition-transform duration-200 group-hover:translate-x-1" />
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function TerrainSection() {
  return (
    <section className="relative overflow-hidden py-24 lg:py-32" data-testid="terrain-section">
      <div className="absolute inset-0">
        <img src={IMG.offroad} alt="IshuCar SUV off-road" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070709] via-[#070709]/70 to-[#070709]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">कहीं भी, कभी भी</p>
          <h2 className="font-hindi mt-4 text-3xl font-extrabold text-white sm:text-4xl">ताकत साथ है</h2>
          <p className="mt-3 font-display text-xl font-bold uppercase text-white sm:text-2xl">Every terrain. Every day.</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {TERRAIN.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.06}>
              <div className="rounded-2xl border border-white/10 bg-[#070709]/70 p-5 text-center backdrop-blur-sm transition-colors duration-300 hover:border-[#e6b800]/50" data-testid={`terrain-${i}`}>
                <t.icon size={22} className="mx-auto text-[#e6b800]" />
                <p className="mt-3 font-display text-sm font-bold uppercase text-white">{t.name}</p>
                <p className="font-hindi text-xs text-neutral-400">{t.hindi}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-neutral-500">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PostersSection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="posters-section">
      <SectionHeading
        eyebrow="ब्रोशर · The Posters"
        title="Prices & specs — as promised"
        sub="पूरी जानकारी — variants, prices और features, एक नज़र में।"
      />
      <div className="mt-14 space-y-10">
        {[
          { src: POSTERS.aero, name: "IshuCar AERO D — प्रीमियम डीज़ल SUV" },
          { src: "/images/suv-poster-hero.jpg", name: "IshuCar SUV — Power Meets Purpose" },
          { src: POSTERS.electric, name: "IshuCar Electric — 100% Electric · 700+ km" },
        ].map((p, i) => (
          <Reveal key={p.name} delay={i * 0.1}>
            <div className="overflow-hidden rounded-2xl border border-white/10" data-testid={`poster-${i}`}>
              <a href={p.src} target="_blank" rel="noopener noreferrer" data-testid={`poster-link-${i}`}>
                <img src={p.src} alt={p.name} loading="lazy" className="w-full object-cover" />
              </a>
              <p className="bg-[#111116] px-5 py-3 text-center font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                {p.name}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BookingCounter() {
  const TARGET = 100001;
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || raf) return;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / 2000);
          setN(Math.round(TARGET * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return (
    <section ref={ref} className="relative overflow-hidden border-y border-[#e6b800]/15 bg-[#0a0a0d]" data-testid="booking-counter">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-5 py-12 text-center sm:px-8">
        <p className="font-display text-4xl font-extrabold text-[#e6b800] sm:text-6xl" data-testid="booking-counter-number">
          {n.toLocaleString("en-IN")}+
        </p>
        <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-neutral-400" data-testid="booking-counter-label">
          Pre-bookings done · लाखों परिवारों का भरोसा
        </p>
        <Link
          to="/booking"
          className="mt-2 rounded-full border border-[#e6b800]/40 px-5 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#e6b800] transition-colors duration-200 hover:bg-[#e6b800] hover:text-black"
          data-testid="booking-counter-link"
        >
          अपनी seat बुक करें · ₹10,000
        </Link>
        <div className="mt-8 w-full max-w-xl" data-testid="counter-countdown">
          <p className="mb-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Delivery January 2028 · बस इतने दिन बचे
          </p>
          <Countdown />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee
        items={["Power Meets Purpose", "Premium Diesel SUV", "7 Seater", "All Terrain Ready", "360° Safety", "BS6 Phase 2"]}
      />
      <BookingCounter />
      <ModelsSection />
      <Variants />
      <EmiCalculator />
      <BentoFeatures />
      <TerrainSection />
      <CabinShowcase />
      <PostersSection />
      <GallerySpotlight />
      <UspStrip />
      <HotspotSection />
      <ReserveCTA />
    </>
  );
}
